import React, {useEffect, useState, useMemo} from 'react'
import * as am4core from '@amcharts/amcharts4/core'
import * as am4charts from '@amcharts/amcharts4/charts'
import am4themes_animated from '@amcharts/amcharts4/themes/animated'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from '@material-ui/core'
import axios from 'axios'

am4core.useTheme(am4themes_animated)

interface FundingBySubCategory {
  subcategory: string
  total_funding: number
}

const FundingBySubCategory: React.FC = () => {
  const [data, setData] = useState<FundingBySubCategory[]>([])
  const [charts, setCharts] = useState<{
    pieChart: am4charts.PieChart | null
  }>({pieChart: null})

  useEffect(() => {
    fetchData()
    return () => {
      if (charts.pieChart) {
        console.log('Disposing chart', charts.pieChart)
        charts.pieChart.dispose()
      }
    }
  }, [])
  // Empty dependency array ensures this runs once.

  const fetchData = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_ENDPOINT + '/funding-by-subcategory'
      )
      const filteredData = response.data.filter(
        (item: FundingBySubCategory) =>
          item.subcategory.trim() !== '' && item.total_funding !== null
      )
      setData(filteredData)
      createCharts(filteredData)
    } catch (error) {
      console.error('Error fetching data: ', error)
    }
  }

  const createCharts = (chartData: FundingBySubCategory[]) => {
    const pieChart = am4core.create('piechartdiv', am4charts.PieChart)
    pieChart.data = chartData

    const pieSeries = pieChart.series.push(new am4charts.PieSeries())
    pieSeries.dataFields.value = 'total_funding'
    pieSeries.dataFields.category = 'subcategory'
    pieSeries.labels.template.disabled = true

    // Customize Legend
    pieChart.legend = new am4charts.Legend()
    pieChart.legend.position = 'right'
    pieChart.legend.scrollable = true
    pieChart.legend.labels.template.fontSize = 16 // Increase font size
    pieChart.legend.labels.template.text = "{name}: ${value.formatNumber('#,###')}" // Include formatted funding amounts

    // Tooltip customization
    pieSeries.slices.template.tooltipText = '{category}: ${value.value.formatNumber("#,###")}'

    // Other existing settings
    pieSeries.slices.template.stroke = am4core.color('#fff')
    pieSeries.slices.template.strokeWidth = 1
    pieSeries.slices.template.strokeOpacity = 1
    pieChart.legend.width = am4core.percent(65)
    // Your existing color settings and so on...

    // Set the colors for the pie chart
    pieSeries.colors.list = [
      am4core.color('#e4ff1a'), // Brightest yellow-green
      am4core.color('#c2db16'),
      am4core.color('#9fb812'),
      am4core.color('#7d950f'), // Darkest yellow-green

      am4core.color('#6fc0e4'), // Lightest blue
      am4core.color('#479bc4'),
      am4core.color('#1e6a9c'),
      am4core.color('#0c3d5d'), // Darker blue

      am4core.color('#3dd6d0'), // Brightest teal
      am4core.color('#2ab8a7'),
      am4core.color('#1b9a7f'),
      am4core.color('#0b7d56'), // Darkest teal

      am4core.color('#16384a'), // Other shades of blue
      am4core.color('#122b36'),
    ]

    setCharts({pieChart})
  }

  const formatCurrency = useMemo(() => {
    return (value: number | string | null) => {
      if (value === null) {
        return ''
      }
      const numValue = typeof value === 'string' ? parseFloat(value) : value
      return '$' + numValue.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')
    }
  }, [])

  return (
    <div className='card'>
      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', height: '80vh'}}>
        <Typography variant='h4' align='center' gutterBottom style={{marginTop: '1em'}}>
          Colorado IIJA - Funding by Sub-Category
        </Typography>

        <div style={{display: 'flex', width: '100%'}}>
          <div id='piechartdiv' style={{flex: 2, height: '70vh'}}></div>
        </div>

        <Typography variant='body2' align='center' gutterBottom style={{marginTop: '1em'}}>
          Invest.gov (2024). Investing in America Map. Retrieved from
          https://www.whitehouse.gov/wp-content/uploads/2023/11/Invest.gov_PublicInvestments_Map_Data_CURRENT.xlsx.
        </Typography>
      </div>
    </div>
  )
}

export default React.memo(FundingBySubCategory)
